/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : ScopeBadge.tsx
// 설명 : 공지 공유범위(X1002) 배지 - 라벨/아이콘/색상을 공통코드에서 가져옴(하드코딩 없음, 2026-10-04)
//  - 라벨 : ZA_MINOR(X1002).MINOR_NM
//  - 아이콘/글자색/배경색 : ZA_MINOR_DTL(X1002) 상세속성 ICON / COLOR_FG / COLOR_BG
//  - 앱 전체에서 한 번만 불러와 캐시함(모듈 레벨). 공통코드 조회 실패 시 다음 렌더 때 재시도.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export type ScopeMeta = { label: string; icon: string; fg: string; bg: string };
type ScopeMetaMap = Record<string, ScopeMeta>;

const SCOPE_MAJOR = 'X1002';
// DB(ZA_MINOR / ZA_MINOR_DTL)에 값이 없거나 아직 불러오기 전일 때 쓰는 기본값
const SCOPE_DEFAULTS: ScopeMetaMap = {
  A: { label: '전사', icon: 'fas fa-building', fg: '#4338ca', bg: '#eef2ff' },
  B: { label: '사업단', icon: 'fas fa-users', fg: '#047857', bg: '#ecfdf5' },
  C: { label: '공통', icon: 'fas fa-globe', fg: '#c2410c', bg: '#fff7ed' },
};
let scopeMetaCache: Promise<ScopeMetaMap> | null = null;

const loadScopeMeta = (): Promise<ScopeMetaMap> => {
  if (!scopeMetaCache) {
    scopeMetaCache = Promise.all([
      axios.post(`${API_BASE_URL}/com/apiComboBox`, { hcode: SCOPE_MAJOR }),
      axios.post(`${API_BASE_URL}/com/apiMinorDtl`, { major: SCOPE_MAJOR }),
    ]).then(([cb, dtl]) => {
      const map: ScopeMetaMap = {};
      for (const o of (cb.data?.rsResults || []) as { value: string; label: string }[]) {
        map[o.value] = { label: o.label, icon: '', fg: '', bg: '' };
      }
      for (const d of (dtl.data?.rsResults || []) as { minor_cd: string; dtl_cd: string; dtl_nm: string }[]) {
        const m = map[d.minor_cd];
        if (!m) continue;
        if (d.dtl_cd === 'ICON') m.icon = d.dtl_nm;
        if (d.dtl_cd === 'COLOR_FG') m.fg = d.dtl_nm;
        if (d.dtl_cd === 'COLOR_BG') m.bg = d.dtl_nm;
      }
      return map;
    }).catch(() => {
      scopeMetaCache = null;
      return {};
    });
  }
  return scopeMetaCache;
};

export const useScopeMeta = (): ScopeMetaMap => {
  const [map, setMap] = useState<ScopeMetaMap>({});
  useEffect(() => { loadScopeMeta().then(setMap); }, []);
  return map;
};

// 공유범위 라벨만 필요할 때(툴팁/문자열 조합 등)
export const useScopeLabel = () => {
  const map = useScopeMeta();
  return (scopeTp: string) => map[scopeTp]?.label ?? SCOPE_DEFAULTS[scopeTp]?.label ?? '';
};

type ScopeBadgeProps = {
  scopeTp?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ScopeBadge: React.FC<ScopeBadgeProps> = ({ scopeTp, className, style }) => {
  const key = scopeTp ?? '';
  const meta = useScopeMeta()[key] ?? SCOPE_DEFAULTS[key];
  if (!meta) return null;
  return (
    <span className={className} title={meta.label}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 999,
               fontSize: 11, fontWeight: 600, background: meta.bg, color: meta.fg, ...style }}>
      {meta.icon && <i className={meta.icon}></i>}{meta.label}
    </span>
  );
};

export default ScopeBadge;
